import { useState, useCallback, useEffect } from "react";
import { useStickyTourStore } from "../../stores/stickyTourStore";
import { AnchorIds } from "./TourTypes";

interface UserData {
  update: (data: any) => void;
  data?: {
    needsDemo?: boolean;
    onboarding?: {
      storyBible?: {
        farthestStepId?: string;
        hasGenerated?: string[];
      };
    };
  };
}

interface StickyTourDebugMenuProps {
  userData?: UserData;
}

const StickyTourDebugMenu = ({ userData }: StickyTourDebugMenuProps) => {
  const [position, setPosition] = useState({ x: 20, y: 20 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isUserDataCollapsed, setIsUserDataCollapsed] = useState(true);

  const activeTourId = useStickyTourStore((state) => state.activeTourId);
  const activeTourVariation = useStickyTourStore((state) => state.activeTourVariation);
  const phase = useStickyTourStore((state) => state.phase);
  const pendingTour = useStickyTourStore((state) => state.pendingTour);
  const cleaningUpTourId = useStickyTourStore((state) => state.cleaningUpTourId);
  const steps = useStickyTourStore((state) => state.steps);
  const currentStepIndex = useStickyTourStore((state) => state.currentStepIndex);
  const anchors = useStickyTourStore((state) => state.anchors);
  const setToStepById = useStickyTourStore((state) => state.setToStepById);
  const showAnchors = useStickyTourStore((state) => state.showAnchors);
  const setShowAnchors = useStickyTourStore((state) => state.setShowAnchors);
  const endTour = useStickyTourStore((state) => state.endTour);

  const activeStep = steps[currentStepIndex];

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      setIsDragging(true);
      setDragOffset({
        x: e.clientX - position.x,
        y: e.clientY - position.y,
      });
    },
    [position],
  );

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (isDragging) {
        setPosition({
          x: e.clientX - dragOffset.x,
          y: e.clientY - dragOffset.y,
        });
      }
    },
    [isDragging, dragOffset],
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  useEffect(() => {
    if (isDragging) {
      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleMouseUp);
      return () => {
        window.removeEventListener("mousemove", handleMouseMove);
        window.removeEventListener("mouseup", handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  // Get unique anchors from current step
  const stepAnchors = activeStep?.stickies?.map((sticky) => sticky.anchorId).filter((id): id is AnchorIds | undefined => id !== undefined) || [];

  const uniqueAnchors = [...new Set(stepAnchors)];

  return (
    <div
      style={{
        position: "fixed",
        left: position.x,
        top: position.y,
        zIndex: 120000,
        backgroundColor: "#1a1a2e",
        color: "#eee",
        borderRadius: 8,
        boxShadow: "0 4px 20px rgba(0,0,0,0.4)",
        fontFamily: "monospace",
        fontSize: 12,
        minWidth: 280,
        maxWidth: 400,
        userSelect: "none",
      }}
    >
      {/* Header - draggable */}
      <div
        onMouseDown={handleMouseDown}
        style={{
          padding: "8px 12px",
          backgroundColor: "#16213e",
          borderRadius: "8px 8px 0 0",
          cursor: isDragging ? "grabbing" : "grab",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderBottom: "1px solid #0f3460",
        }}
      >
        <span style={{ fontWeight: "bold" }}>🟨 Sticky Tour Debugger</span>
        <button
          onClick={() => setIsCollapsed(!isCollapsed)}
          style={{
            background: "none",
            border: "none",
            color: "#eee",
            cursor: "pointer",
            fontSize: 14,
          }}
        >
          {isCollapsed ? "▼" : "▲"}
        </button>
      </div>

      {!isCollapsed && (
        <div style={{ padding: 12 }}>
          {/* Show Anchors Toggle */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 12,
              padding: "4px 8px",
              backgroundColor: "#1e293b",
              borderRadius: 4,
            }}
          >
            <span style={{ color: "#e5e7eb" }}>Show Anchors</span>
            <button
              onClick={() => setShowAnchors(!showAnchors)}
              style={{
                padding: "2px 8px",
                fontSize: 10,
                border: "none",
                borderRadius: 4,
                cursor: "pointer",
                backgroundColor: showAnchors ? "#4ade80" : "#374151",
                color: showAnchors ? "#000" : "#eee",
                fontWeight: "bold",
              }}
            >
              {showAnchors ? "ON" : "OFF"}
            </button>
          </div>

          {/* Tour Info */}
          <div style={{ marginBottom: 12 }}>
            <div style={{ color: "#94a3b8", marginBottom: 4 }}>
              <strong>Phase:</strong>{" "}
              <span
                style={{
                  color: phase === "active" ? "#4ade80" : phase === "idle" ? "#6b7280" : phase === "setting-up" ? "#fbbf24" : "#f87171",
                }}
              >
                {phase}
              </span>
            </div>
            <div style={{ color: "#94a3b8", marginBottom: 4 }}>
              <strong>Tour:</strong> <span style={{ color: activeTourId ? "#4ade80" : "#f87171" }}>{activeTourId || "None"}</span>
            </div>
            <div style={{ color: "#94a3b8", marginBottom: 4 }}>
              <strong>Variation:</strong> <span style={{ color: "#60a5fa" }}>{activeTourVariation || "None"}</span>
            </div>
            {cleaningUpTourId && (
              <div style={{ color: "#94a3b8", marginBottom: 4 }}>
                <strong>Cleaning up:</strong> <span style={{ color: "#f87171" }}>{cleaningUpTourId}</span>
              </div>
            )}
            {pendingTour && (
              <div style={{ color: "#94a3b8" }}>
                <strong>Pending:</strong> <span style={{ color: "#fbbf24" }}>{pendingTour.tour.id}</span>
              </div>
            )}
          </div>

          {/* Step Buttons */}
          {steps.length > 0 && (
            <div style={{ marginBottom: 12 }}>
              <div style={{ color: "#94a3b8", marginBottom: 6, fontWeight: "bold" }}>
                Steps ({currentStepIndex + 1}/{steps.length}):
              </div>
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 4,
                  maxHeight: 150,
                  overflowY: "auto",
                }}
              >
                {steps.map((step, i) => (
                  <button
                    key={step.id}
                    onClick={() => setToStepById(step.id)}
                    title={step.id}
                    style={{
                      padding: "4px 8px",
                      fontSize: 11,
                      border: "none",
                      borderRadius: 4,
                      cursor: "pointer",
                      backgroundColor: i === currentStepIndex ? "#4ade80" : "#374151",
                      color: i === currentStepIndex ? "#000" : "#eee",
                      fontWeight: i === currentStepIndex ? "bold" : "normal",
                      transition: "all 0.15s",
                    }}
                  >
                    {i}: {step.id.length > 20 ? step.id.slice(0, 20) + "..." : step.id}
                  </button>
                ))}
              </div>
            </div>
          )}
          {/* Exit Current Tour Button */}
          {phase === "active" && (
            <button
              onClick={() => endTour(true)}
              disabled={!activeTourId || phase !== "active"}
              style={{
                marginTop: 8,
                marginBottom: 16,
                padding: "4px 8px",
                fontSize: 10,
                border: "none",
                borderRadius: 4,
                cursor: activeTourId && phase === "active" ? "pointer" : "not-allowed",
                backgroundColor: activeTourId && phase === "active" ? "#374151" : "#262626",
                color: activeTourId && phase === "active" ? "#eee" : "#6b7280",
              }}
            >
              Exit Tour
            </button>
          )}

          {/* Current Step Anchors */}
          {activeStep && (
            <div style={{ marginBottom: 12 }}>
              <div style={{ color: "#94a3b8", marginBottom: 6, fontWeight: "bold" }}>Anchors:</div>
              {uniqueAnchors.length > 0 ? (
                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  {uniqueAnchors.map((anchorId) => {
                    const anchorRef = anchors[anchorId as AnchorIds];
                    const isFound = anchorRef?.current !== null && anchorRef?.current !== undefined;
                    return (
                      <div
                        key={anchorId}
                        style={{
                          padding: "4px 8px",
                          backgroundColor: isFound ? "#064e3b" : "#7f1d1d",
                          borderRadius: 4,
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <span style={{ color: "#e5e7eb" }}>{anchorId}</span>
                        <span>{isFound ? "✅" : "❌"}</span>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div style={{ color: "#6b7280", fontStyle: "italic" }}>No anchors (fixed position)</div>
              )}
            </div>
          )}

          {/* Listening Signals */}
          {activeStep && (
            <div>
              <div style={{ color: "#94a3b8", marginBottom: 6, fontWeight: "bold" }}>Listening for:</div>
              {activeStep.listeningFor && activeStep.listeningFor.length > 0 ? (
                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  {activeStep.listeningFor.map((signal) => (
                    <div
                      key={signal}
                      style={{
                        padding: "4px 8px",
                        backgroundColor: "#1e3a5f",
                        borderRadius: 4,
                        color: "#93c5fd",
                      }}
                    >
                      📡 {signal}
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ color: "#6b7280", fontStyle: "italic" }}>No signals (manual progression)</div>
              )}
            </div>
          )}

          {/* No active tour message */}
          {!activeTourId && <div style={{ color: "#6b7280", fontStyle: "italic", textAlign: "center" }}>No active tour</div>}

          {/* Onboarding UserData Section */}
          {userData && (
            <div style={{ marginTop: 12, borderTop: "1px solid #374151", paddingTop: 12 }}>
              <div
                onClick={() => setIsUserDataCollapsed(!isUserDataCollapsed)}
                style={{
                  color: "#94a3b8",
                  marginBottom: isUserDataCollapsed ? 0 : 6,
                  fontWeight: "bold",
                  cursor: "pointer",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <span>Onboarding UserData:</span>
                <span style={{ fontSize: 10 }}>{isUserDataCollapsed ? "▼" : "▲"}</span>
              </div>

              {!isUserDataCollapsed && (
                <>
                  {/* Needs Demo */}
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginBottom: 8,
                      padding: "4px 8px",
                      backgroundColor: "#1e293b",
                      borderRadius: 4,
                    }}
                  >
                    <span style={{ color: "#e5e7eb" }}>
                      needsDemo: <span style={{ color: userData.data?.needsDemo ? "#4ade80" : "#f87171" }}>{String(!!userData.data?.needsDemo)}</span>
                    </span>
                    {!userData.data?.needsDemo && (
                      <button
                        onClick={() => userData.update({ needsDemo: true })}
                        style={{
                          padding: "2px 8px",
                          fontSize: 10,
                          border: "none",
                          borderRadius: 4,
                          cursor: "pointer",
                          backgroundColor: "#374151",
                          color: "#eee",
                        }}
                      >
                        Set true
                      </button>
                    )}
                  </div>

                  {/* Story Bible Farthest Step */}
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      gap: 4,
                      padding: "4px 8px",
                      backgroundColor: "#1e293b",
                      borderRadius: 4,
                      justifyContent: "space-between",
                    }}
                  >
                    <div style={{ color: "#e5e7eb" }}>
                      farthestStepId: <span style={{ color: "#60a5fa" }}>{userData.data?.onboarding?.storyBible?.farthestStepId || "null"}</span>
                    </div>
                    {userData.data?.onboarding?.storyBible?.farthestStepId && (
                      <button
                        onClick={() => userData.update({ "onboarding.storyBible.farthestStepId": null })}
                        style={{
                          padding: "2px 8px",
                          fontSize: 10,
                          border: "none",
                          borderRadius: 4,
                          cursor: "pointer",
                          backgroundColor: "#374151",
                          color: "#eee",
                        }}
                      >
                        Reset
                      </button>
                    )}
                  </div>

                  {/* Reset All Onboarding Button */}
                  {(!userData.data?.needsDemo || userData.data?.onboarding?.storyBible?.farthestStepId) && (
                    <button
                      onClick={() => {
                        userData.update({ needsDemo: true });
                        userData.update({ "onboarding.storyBible.farthestStepId": null });
                      }}
                      style={{
                        marginTop: 8,
                        padding: "4px 8px",
                        fontSize: 10,
                        border: "none",
                        borderRadius: 4,
                        cursor: "pointer",
                        backgroundColor: "#374151",
                        color: "#eee",
                      }}
                    >
                      Reset Onboarding
                    </button>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default StickyTourDebugMenu;
